import { NoticeInfo } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { Alert, colors, LabelTooltip, Paragraph, Row, Separator, Skeleton, Toggle, ToggleGroup } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { getDisabledTooltipMessage, getLanguageName } from './utils/translation.utils'

/**
 * Component that displays transcript translation with toggle control.
 * Shows loading states, errors, and handles user interaction for enabling/disabling translation.
 */
const Translation = ({
  translationCache,
  currentImageArk,
  isTranslationAvailable,
  isTranslationEnabled,
  onToggleTranslation,
  hasTranslationError,
  hasArtifactError,
  isTranslating,
  hasTranscriptText,
  isTranscriptReady,
}) => {
  const [t] = useTranslation()

  // Determine display states
  const hasAnyError = hasArtifactError || hasTranslationError
  const shouldDisableToggle = !isTranslationAvailable || isTranslating
  const currentTranslation = translationCache[currentImageArk]
  const shouldShowLoadingSkeleton = isTranslating && isTranscriptReady
  const shouldShowTranslationContent = isTranslationEnabled && !hasTranslationError

  // Tooltip messages
  const disabledTooltipMessage = getDisabledTooltipMessage({
    isTranslationAvailable,
    hasArtifactError,
    hasTranslationError,
    hasTranscriptText,
    t,
  })

  const infoTooltipMessage = isTranslationAvailable
    ? t('full-text.translation-info', 'To change the language, use the World icon in the FamilySearch header.')
    : undefined

  // Toggle label
  const toggleLabel = isTranslationAvailable
    ? t('full-text.show-translation-with-language', 'Show Translation ({language})', {
        language: getLanguageName(i18n.language),
      })
    : t('full-text.show-translation', 'Show Translation')

  return (
    <>
      {/* Translation Toggle */}
      <Row alignY="middle" gutters="nano">
        <LabelTooltip
          label={disabledTooltipMessage}
          target={
            <ToggleGroup dense>
              <Toggle
                label={
                  <Row gutters="nano">
                    {toggleLabel}
                    <LabelTooltip
                      label={infoTooltipMessage}
                      target={<NoticeInfo size="xs" color={shouldDisableToggle ? colors.gray40 : colors.blue60} />}
                    />
                  </Row>
                }
                name="show-translation-toggle"
                checked={isTranslationEnabled}
                onChange={onToggleTranslation}
                disabled={shouldDisableToggle}
                data-testid="full-text-toggle-translation"
              />
            </ToggleGroup>
          }
        />
      </Row>

      {/* Error Alert */}
      {hasAnyError && (
        <>
          <Separator size="xs" />
          <Alert type="error" dismissable>
            {t('full-text.translation-error', 'Unable to translate transcript. Reload page.')}
          </Alert>
          <Separator size="xs" />
        </>
      )}

      {/* Translation Content */}
      {shouldShowTranslationContent && (
        <>
          <Separator size="xs" />
          <div>
            {shouldShowLoadingSkeleton && <Skeleton.Paragraph />}
            {currentTranslation && (
              <>
                <Paragraph size="sm" dense>
                  {t('full-text.translation-ai-generated', 'AI-generated translation')}
                </Paragraph>
                <Paragraph size="sm" dense>
                  {currentTranslation}
                </Paragraph>
              </>
            )}
          </div>
          <Separator size="xs" />
        </>
      )}

      <Separator size="xxs" />
    </>
  )
}

export default Translation
